import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "../App";
import { Award, Download, X, Star, BookOpen, CheckCircle } from "lucide-react";

export function CertificateChecker({ childId, childName }) {
  const [certData, setCertData] = useState(null);

  useEffect(() => {
    if (!childId) return;
    axios.get(`${API}/achievements/certificates/check/${childId}`)
      .then(res => setCertData(res.data))
      .catch(() => {});
  }, [childId]);

  if (!certData) return null;

  const { lessons_completed, lessons_required, tasks_completed = 0, tasks_required = 15, certificate_earned, certificate } = certData;
  const lessonProgress = Math.min((lessons_completed / lessons_required) * 100, 100);
  const taskProgress = Math.min((tasks_completed / tasks_required) * 100, 100);

  if (!certificate_earned) {
    return (
      <div className="bg-white rounded-2xl shadow-md p-3 border-l-4" style={{ borderColor: "#9B5DE5" }}
        data-testid="certificate-progress">
        <div className="flex items-center gap-2 mb-2">
          <Award size={18} color="#9B5DE5" strokeWidth={2.5} />
          <span className="text-sm font-black text-[#073B4C]" style={{ fontFamily: "Fredoka, sans-serif" }}>
            Weekly Certificate
          </span>
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <BookOpen size={12} color="#118AB2" />
            <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all duration-500"
                style={{ width: `${lessonProgress}%`, background: "linear-gradient(90deg, #118AB2, #06D6A0)" }} />
            </div>
            <span className="text-[10px] font-bold text-gray-500 min-w-[50px] text-right">
              {lessons_completed}/{lessons_required} lessons
            </span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle size={12} color="#9B5DE5" />
            <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all duration-500"
                style={{ width: `${taskProgress}%`, background: "linear-gradient(90deg, #9B5DE5, #EF476F)" }} />
            </div>
            <span className="text-[10px] font-bold text-gray-500 min-w-[50px] text-right">
              {tasks_completed}/{tasks_required} activities
            </span>
          </div>
        </div>
        <p className="text-xs text-gray-400 mt-1.5">Complete all lessons & activities to earn your certificate!</p>
      </div>
    );
  }

  return <CertificateBadge certificate={certificate} childName={childName} />;
}

function CertificateBadge({ certificate, childName }) {
  const [showCert, setShowCert] = useState(false);

  return (
    <>
      <button
        onClick={() => setShowCert(true)}
        className="w-full bg-gradient-to-r from-yellow-400 to-orange-400 rounded-2xl p-3 shadow-lg border-b-4 border-yellow-500 hover:scale-[1.02] active:scale-[0.98] transition-all text-left"
        data-testid="certificate-earned-badge"
      >
        <div className="flex items-center gap-3">
          <div className="bg-white/30 rounded-xl p-2">
            <Award size={24} color="white" strokeWidth={2.5} />
          </div>
          <div className="flex-1">
            <p className="text-white font-black text-sm" style={{ fontFamily: "Fredoka, sans-serif" }}>
              Certificate Earned!
            </p>
            <p className="text-white/80 text-xs font-medium">
              Week {certificate.week_number}: {certificate.theme}
            </p>
          </div>
          <span className="text-white/80 font-bold text-xs bg-white/20 rounded-lg px-2 py-1">View</span>
        </div>
      </button>

      {showCert && (
        <CertificateModal
          certificate={certificate}
          childName={childName}
          onClose={() => setShowCert(false)}
        />
      )}
    </>
  );
}

function CertificateModal({ certificate, childName, onClose }) {
  const certRef = useRef(null);

  const handlePrint = () => {
    const content = certRef.current;
    if (!content) return;
    const win = window.open("", "_blank");
    win.document.write(`
      <html><head><title>Bible Buds Certificate</title>
      <style>
        body { margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; background: #f0f0f0; }
        .cert { width: 800px; padding: 60px; background: white; border: 8px solid #9B5DE5; border-radius: 20px; text-align: center; font-family: Georgia, serif; position: relative; }
        .cert::before { content: ''; position: absolute; inset: 8px; border: 3px solid #FFD166; border-radius: 14px; }
        h1 { color: #9B5DE5; font-size: 42px; margin-bottom: 8px; }
        h2 { color: #073B4C; font-size: 28px; margin: 24px 0 8px; }
        .name { color: #EF476F; font-size: 36px; font-weight: bold; margin: 16px 0; border-bottom: 3px solid #EF476F; display: inline-block; padding-bottom: 4px; }
        .theme { color: #118AB2; font-size: 20px; margin: 12px 0; }
        .scripture { color: #666; font-style: italic; font-size: 16px; margin: 8px 0 24px; }
        .stars { font-size: 32px; margin: 16px 0; }
        .date { color: #999; font-size: 14px; margin-top: 32px; }
        @media print { body { background: white; } .cert { border-width: 4px; } }
      </style></head><body>
      <div class="cert">
        <div class="stars">&#9733; &#9733; &#9733; &#9733; &#9733;</div>
        <h1>Bible Buds</h1>
        <h2>Certificate of Completion</h2>
        <p style="color:#666;font-size:18px;">This certifies that</p>
        <div class="name">${childName || certificate.child_name}</div>
        <p style="color:#666;font-size:18px;">has successfully completed all lessons for</p>
        <div class="theme">Week ${certificate.week_number}: ${certificate.theme}</div>
        <div class="scripture">${certificate.scripture}</div>
        <div class="stars">&#9733; &#9733; &#9733; &#9733; &#9733;</div>
        <div class="date">${new Date(certificate.earned_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</div>
      </div>
      <script>window.print();</script>
      </body></html>
    `);
    win.document.close();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="p-4 flex items-center justify-between" style={{ background: "linear-gradient(135deg, #9B5DE5, #EF476F)" }}>
          <div className="flex items-center gap-2">
            <Award size={24} color="white" />
            <h2 className="text-white font-black text-lg" style={{ fontFamily: "Fredoka, sans-serif" }}>
              Weekly Certificate
            </h2>
          </div>
          <button onClick={onClose} className="bg-white/20 hover:bg-white/30 rounded-xl p-2 transition-all">
            <X size={20} color="white" />
          </button>
        </div>

        {/* Certificate Preview */}
        <div className="p-6" ref={certRef}>
          <div className="border-4 border-[#9B5DE5] rounded-2xl p-6 text-center relative">
            <div className="absolute inset-2 border-2 border-[#FFD166] rounded-xl pointer-events-none" />
            <div className="flex justify-center gap-1 mb-3">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={24} color="#FFD166" fill="#FFD166" />
              ))}
            </div>
            <h3 className="text-2xl font-black text-[#9B5DE5]" style={{ fontFamily: "Fredoka, sans-serif" }}>
              Bible Buds
            </h3>
            <p className="text-lg font-bold text-[#073B4C] mt-3">Certificate of Completion</p>
            <p className="text-sm text-gray-500 mt-2">This certifies that</p>
            <p className="text-2xl font-black text-[#EF476F] mt-1 border-b-2 border-[#EF476F] inline-block pb-1"
              style={{ fontFamily: "Fredoka, sans-serif" }}>
              {childName || certificate.child_name}
            </p>
            <p className="text-sm text-gray-500 mt-3">has completed all lessons for</p>
            <p className="text-lg font-bold text-[#118AB2] mt-1">
              Week {certificate.week_number}: {certificate.theme}
            </p>
            <p className="text-sm text-gray-400 italic mt-1">{certificate.scripture}</p>
            <div className="flex justify-center gap-1 mt-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={20} color="#FFD166" fill="#FFD166" />
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-4">
              {new Date(certificate.earned_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="px-6 pb-6">
          <button
            onClick={handlePrint}
            className="w-full py-3 rounded-xl font-bold text-white flex items-center justify-center gap-2 hover:scale-[1.02] active:scale-[0.98] transition-all"
            style={{ background: "linear-gradient(90deg, #9B5DE5, #EF476F)" }}
            data-testid="print-certificate-btn"
          >
            <Download size={18} />
            Print Certificate
          </button>
        </div>
      </div>
    </div>
  );
}

export function CertificatesList({ childId }) {
  const [certificates, setCertificates] = useState([]);

  useEffect(() => {
    if (!childId) return;
    axios.get(`${API}/achievements/certificates/${childId}`)
      .then(res => setCertificates(res.data))
      .catch(() => {});
  }, [childId]);

  if (certificates.length === 0) return null;

  return (
    <div className="space-y-2" data-testid="certificates-list">
      {certificates.map(cert => (
        <div key={cert.id} className="flex items-center gap-3 bg-yellow-50 rounded-xl p-2.5 border border-yellow-100">
          <Award size={18} color="#F77F00" />
          <div className="flex-1">
            <p className="text-sm font-bold text-gray-800">Week {cert.week_number}: {cert.theme}</p>
            <p className="text-xs text-gray-500">{new Date(cert.earned_at).toLocaleDateString()}</p>
          </div>
          <BookOpen size={14} color="#9B5DE5" />
        </div>
      ))}
    </div>
  );
}
